import { ActionIcon, Button, Group, Loader, Menu, Table, Text, Tooltip } from '@mantine/core';
import { IconDots, IconDownload, IconPlus } from '@tabler/icons-react';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useCurrentClient } from 'src/clients/context/CurrentClientProvider';
import ConfirmDeleteModal from 'src/common/components/ConfirmDeleteModal';
import { useEmailDocument } from 'src/common/hooks/useEmailDocument';
import EmailDocumentButton from 'src/components/convenience/buttons/EmailDocumentButton';
import dataCaptureClasses from 'src/data-capture/DataCapture.module.css';
import { useFlags } from 'src/feature-flags/FlagsHook';
import { useDeleteMeetingNote } from 'src/meeting-note/hooks/useDeleteMeetingNote';
import { useMeetingNotes } from 'src/meeting-note/hooks/useMeetingNotes';
import { useIsNoteSubstantiating } from 'src/meetings/hooks/useSubstantiateMeetingNote';
import { GenericErrorPage } from '../../error/GenericErrorPage';
import PageLoader from '../../UI/loader/PageLoader';
import { useFileTemporaryLinkMutation } from '../hooks/useFileTemporaryLinkMutation';
import SuitabilityReportStatusPill from './SuitabilityReportStatusPill';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import FileUploadLayout from './FileUploadLayout';
import EmptyView from './components/EmptyView';
import FileNameWithIcon from './files-table/FileNameWithIcon';
import classes from './files-table/FilesTable.module.css';
import { dateMatchesSearch, formatDateTime } from 'src/meetings/utils/date-utils';

export default function MeetingNotes() {
  const { meetingNotes, isLoading, isError, error, refetch } = useMeetingNotes();
  const { clientId, client } = useCurrentClient();
  const {
    createLink,
    isLoading: isCreatingLink,
    isError: creatingLinkFailed,
  } = useFileTemporaryLinkMutation();
  const {
    deleteMeetingNote,
    error: deleteMeetingNoteError,
    reset: resetDeleteMeetingNote,
    isLoading: isDeletingMeetingNote,
  } = useDeleteMeetingNote();
  const { flagEnabledMeetingNoteEdit } = useFlags();
  const navigate = useNavigate();

  const openMeetingNoteFlow = () =>
    navigate(`/client/${clientId}/meeting-note`, { state: { fromPath: location.pathname } });
  const { handleEmailClick, handleDeleteEmail, isGenerating, isDeleting } = useEmailDocument();
  const [searchValue, setSearchValue] = useState('');
  const [noteToDelete, setNoteToDelete] = useState<{ id: string; displayName: string } | null>(
    null,
  );
  const [selectedNoteForEmailGeneration, setSelectedNoteForEmailGeneration] = useState<
    null | string
  >();

  const handleDeletePress = (note: { id: string; displayName: string }) => {
    resetDeleteMeetingNote();
    setNoteToDelete(note);
  };

  const handleDownload = async (templatisedFileId: number) => {
    try {
      const link = await createLink(templatisedFileId);
      window.open(link.url, '_blank');
    } catch (e) {
      notifyAndLogError(
        'Failed to download meeting note',
        'An error occurred while trying to download the meeting note. Please try again later.',
        e,
        { clientId, templatisedFileId },
      );
      return;
    }
  };

  const handleDeleteConfirm = async () => {
    if (!noteToDelete) return;
    try {
      await deleteMeetingNote(clientId, noteToDelete.id);
    } catch (e) {
      notifyAndLogError(
        'Failed to delete meeting note',
        'An error occurred while trying to delete the meeting note. Please try again later.',
        e,
        { clientId, reportId: noteToDelete.id },
      );
    }
    setNoteToDelete(null);
    await refetch();
  };

  const filteredMeetingNotes = searchValue.trim()
    ? meetingNotes.filter(meetingNote => {
        const searchLower = searchValue.toLowerCase();
        return (
          (meetingNote.displayName || '').toLowerCase().includes(searchLower) ||
          dateMatchesSearch(meetingNote.createdAt, searchLower)
        );
      })
    : meetingNotes;

  const handleEmailGenerationClick = async (noteId: string) => {
    setSelectedNoteForEmailGeneration(noteId);
    return await handleEmailClick(noteId, 'MEETING_NOTE');
  };

  const rows = filteredMeetingNotes.map(meetingNote => (
    <Table.Tr key={meetingNote.id}>
      <Table.Td className={classes.fileNameCell}>
        <FileNameWithIcon
          fileName={meetingNote.displayName}
          isFolder={false}
          isGenerated={meetingNote.isGenerated}
        />
      </Table.Td>
      <Table.Td>
        <Text size="sm">{formatDateTime(meetingNote.createdAt)}</Text>
      </Table.Td>
      <Table.Td>
        <SuitabilityReportStatusPill status={meetingNote.backgroundProcess?.status ?? 'SUCCESS'} />
      </Table.Td>
      <Table.Td align="right">
        <Group justify="space-between" wrap="nowrap">
          <Group gap="xs" wrap="nowrap">
            {flagEnabledMeetingNoteEdit &&
              meetingNote.documentHistoryId &&
              (meetingNote.backgroundProcess?.status ?? 'SUCCESS') === 'SUCCESS' && (
                <MeetingNoteEditButton noteId={meetingNote.id} clientId={clientId} />
              )}
            {meetingNote.templatisedFileId && (
              <>
                <EmailDocumentButton
                  documentId={meetingNote.id}
                  isGenerating={isGenerating && selectedNoteForEmailGeneration === meetingNote.id}
                  isDeleting={isDeleting}
                  onEmailClick={async () => await handleEmailGenerationClick(meetingNote.id)}
                  onDelete={handleDeleteEmail}
                  clientName={client ? `${client.firstName} ${client.lastName}` : undefined}
                  documentLabel={meetingNote.displayName}
                />
                <Tooltip label="Download">
                  <ActionIcon
                    variant={
                      flagEnabledMeetingNoteEdit && meetingNote.documentHistoryId
                        ? 'secondary'
                        : 'primary'
                    }
                    size="lg"
                    onClick={() => handleDownload(meetingNote.templatisedFileId!)}
                  >
                    <IconDownload size={18} />
                  </ActionIcon>
                </Tooltip>
              </>
            )}
          </Group>
          <Menu
            transitionProps={{ transition: 'pop-top-right' }}
            position="bottom-end"
            width={220}
            withinPortal
          >
            <Menu.Target>
              <IconDots color="#475467" stroke={2} size={20} />
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Item onClick={() => handleDeletePress(meetingNote)}>Delete</Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </Group>
      </Table.Td>
    </Table.Tr>
  ));

  if (isError || creatingLinkFailed) {
    return <GenericErrorPage error={error || { creatingLinkFailed }} />;
  }

  if (isCreatingLink) {
    return <PageLoader absolute={false} />;
  }

  if (filteredMeetingNotes.length === 0 && !isLoading) {
    return (
      <FileUploadLayout title="Meeting notes">
        <EmptyView
          title="Generate meeting notes"
          subtitle="Generate detailed meeting notes from your recordings with Siesta AI to capture key decisions and action items."
          ctaText="Generate new"
          ctaIcon={IconPlus}
          onCtaClick={openMeetingNoteFlow}
        />
      </FileUploadLayout>
    );
  }

  return (
    <FileUploadLayout
      title="Meeting notes"
      showSearch
      searchValue={searchValue}
      onSearchChange={setSearchValue}
      actions={
        <Button
          variant="primary"
          leftSection={<IconPlus size={16} />}
          size="md"
          onClick={openMeetingNoteFlow}
        >
          Generate
        </Button>
      }
    >
      <Table.ScrollContainer
        minWidth={600}
        type="native"
        className={dataCaptureClasses.tableScrollContainer}
      >
        <Table
          variant="bare"
          captionSide="bottom"
          classNames={{ th: dataCaptureClasses.th }}
          verticalSpacing={'sm'}
        >
          <Table.Thead>
            <Table.Tr>
              <Table.Th>File name</Table.Th>
              <Table.Th>Created</Table.Th>
              <Table.Th>Status</Table.Th>
              <Table.Th></Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {isLoading ? (
              <Table.Tr>
                <Table.Td colSpan={4} style={{ textAlign: 'center', padding: '2rem' }}>
                  <Loader size="sm" />
                </Table.Td>
              </Table.Tr>
            ) : (
              rows
            )}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>

      <ConfirmDeleteModal
        opened={!!noteToDelete}
        onClose={() => setNoteToDelete(null)}
        onConfirm={handleDeleteConfirm}
        title="Delete meeting note"
        message={`Are you sure you want to delete "${noteToDelete?.displayName}"? This action cannot be undone.`}
        error={deleteMeetingNoteError}
        isLoading={isDeletingMeetingNote}
      />
    </FileUploadLayout>
  );
}

function MeetingNoteEditButton({ noteId, clientId }: { noteId: string; clientId: string }) {
  const navigate = useNavigate();
  const isSubstantiating = useIsNoteSubstantiating(noteId);

  return (
    <Tooltip label="Transcription references being prepared" disabled={!isSubstantiating}>
      <Button
        variant="primary"
        disabled={isSubstantiating}
        onClick={() => navigate(`/client/${clientId}/document/meeting-notes/${noteId}/edit`)}
      >
        Edit
      </Button>
    </Tooltip>
  );
}
